import type { AlgoliaJobAdsRequestParameters } from "@/modules/jobAds/client/utils/getAlgoliaFacetFiltersFromParams"
import type {
  JobAdFacetPropertiesFromAlgolia,
  JobAdFacetValuesFromAlgolia,
} from "@/modules/jobAds/types/jobAdFromAlgolia"
import type { MultipleQueriesQuery } from "@algolia/client-search"

import _cloneDeep from "lodash/cloneDeep"
import _omit from "lodash/omit"
import _snakeCase from "lodash/snakeCase"

import {
  ALGOLIA_ATTRIBUTES_TO_RETRIEVE,
  ALGOLIA_FACETS,
  LOCALE_NONE,
  RESULTS_SORT_OPTIONS_MAPPING,
} from "@/modules/jobAds/client/constants"
import { computeAlgoliaOptionalFilters } from "@/modules/jobAds/client/utils/computeAlgoliaOptionalFilters"
import { enabledAlgoliaSponsoredFiltersRules } from "@/modules/jobAds/client/utils/enabledAlgoliaSponsoredFiltersRules"
import { formatAlgoliaFacetValue } from "@/modules/jobAds/client/utils/formatAlgoliaFacetValue"
import { formatFiltersForAlgolia } from "@/modules/jobAds/client/utils/formatFiltersForAlgolia"
import { formatFiltersForFacetQuery } from "@/modules/jobAds/client/utils/formatFiltersForFacetQuery"
import { getAlgoliaFacetFiltersFromBusinessRules } from "@/modules/jobAds/client/utils/getAlgoliaFacetFiltersFromBusinessRules"
import { getAlgoliaFacetFiltersFromParams } from "@/modules/jobAds/client/utils/getAlgoliaFacetFiltersFromParams"
import { getAlgoliaLatLngParams } from "@/modules/jobAds/client/utils/getAlgoliaLatLngParams"
import { getAlgoliaRuleContexts } from "@/modules/jobAds/client/utils/getAlgoliaRuleContexts"
import { computeSavedSearchQuery } from "@/modules/jobSearch/helpers/computeSavedSearchQuery"
import { computeLocationObjectFromSingleQueryParam, getLocationType } from "@/modules/locations/locations"

export type AlgoliaJobAdsRequest = {
  mainQuery?: MultipleQueriesQuery
  sponsoredQuery?: MultipleQueriesQuery
  facetQueries?: MultipleQueriesQuery[]
  savedSearchQuery?: MultipleQueriesQuery
}

export const computeAlgoliaJobAdsRequest = (params: AlgoliaJobAdsRequestParameters): AlgoliaJobAdsRequest => {
  const {
    ccShortName,
    facultySchoolId,
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_INDEX_NAME,
    isLogged,
    lat,
    lng,
    locales,
    location,
    page,
    radius,
    savedSearch,
    schoolId,
    seenJobAds,
    userCountry,
    userQuery,
  } = params

  const locationObject = location ? computeLocationObjectFromSingleQueryParam(location) : null
  const locationType = locationObject ? getLocationType(locationObject) : null

  const cleanLocales = (locales || []).filter(locale => locale !== LOCALE_NONE)
  const cleanParams = {
    ...params,
    locales: cleanLocales,
  }

  const facetFilters = getAlgoliaFacetFiltersFromBusinessRules(
    getAlgoliaFacetFiltersFromParams(cleanParams, userCountry),
    cleanParams
  )

  const isOptionalFilterEnabled = indexName !== RESULTS_SORT_OPTIONS_MAPPING.RECENCY

  const optionalFilters = isOptionalFilterEnabled ? computeAlgoliaOptionalFilters(params, facetFilters) : null

  const filters = formatFiltersForAlgolia(facetFilters)

  const aroundLatLngParams = getAlgoliaLatLngParams(lat, lng, radius)
  const ruleContexts = getAlgoliaRuleContexts({ ccShortName, isLogged, userCountry })

  const sponsoredFilters = enabledAlgoliaSponsoredFiltersRules(filters)

  const commonParams = {
    analytics: true,
    attributesToHighlight: [],
    attributesToRetrieve: ALGOLIA_ATTRIBUTES_TO_RETRIEVE,
    clickAnalytics: true,
    facets: ["noFacets"], // Weird issue with the nbHits when facets are empty (see https://jobteaser.atlassian.net/browse/DUX-4317). When facets are empty, Algolia seems to (wrongly) estimate the number of hits. We have to add a dummy facet name to avoid this issue.
    page,
    ...(locationType === "city" && { ...aroundLatLngParams }),
    ...(optionalFilters && { optionalFilters }),
  }

  const sponsoredQuery = {
    indexName: process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_OF_THE_WEEK_INDEX_NAME,
    params: {
      ...commonParams,
      filters: sponsoredFilters,
      hitsPerPage: 2,
      ruleContexts,
    },
    query: userQuery,
  }

  const mainQuery = {
    indexName,
    params: {
      ...commonParams,
      filters,
      hitsPerPage: 20,
      ruleContexts,
    },
    query: userQuery,
  }

  const facetQueries = (
    Object.entries(ALGOLIA_FACETS) as [JobAdFacetPropertiesFromAlgolia, JobAdFacetValuesFromAlgolia][]
  )
    .filter(([facetKey]) => facetKey !== "LOCATIONS")
    .map(([algoliaFacetProperty, algoliaFacetValue]) => {
      const facetFiltersForFacetQuery = formatFiltersForFacetQuery(facetFilters, algoliaFacetProperty)

      return {
        indexName,
        params: {
          attributesToHighlight: [],
          attributesToRetrieve: ALGOLIA_ATTRIBUTES_TO_RETRIEVE,
          facets: formatAlgoliaFacetValue(algoliaFacetProperty, algoliaFacetValue, facultySchoolId, schoolId),
          filters: facetFiltersForFacetQuery,
          hitsPerPage: 0,
          ...(locationType === "city" && { ...aroundLatLngParams }),
        },
        query: userQuery,
      }
    })

  return {
    facetQueries,
    mainQuery,
    ...(savedSearch && {
      savedSearchQuery: computeSavedSearchQuery(savedSearch, {
        initialParams: cleanParams,
        seenJobAds,
        userCountry,
      }),
    }),
    sponsoredQuery,
  }
}
